import { styled } from '@mui/material'
import { Annotations, Image, Shape } from 'plotly.js'
import Plotly from 'plotly.js/dist/plotly-axon'
import { PlotParams } from 'react-plotly.js'
import createPlotlyComponent from 'react-plotly.js/factory'
import { memo, useMemo, useEffect, useRef } from 'react'
import { usePlotRange } from 'redux/slices/hooks'
import { useCellVisualizationsSlice } from 'redux/slices/hooks/useCellVisualizationsSlice'
import { CellPlotData } from 'redux/slices/types'
import useFlagCondition from 'components/shared/useFlagCondition'
import usePanelHeight from '../cellSelectionDrawer/usePanelHeight'
import { onPlotUpdate } from './onPlotUpdate'
import usePlotDataWithScale from './usePlotDataWithScale'
import useCellVisualizationUrlParams from '../useCellVisualizationUrlParams'
import { calculatePlotRangeWithPadding } from './utils'

// customizable method: use your own `Plotly` object
const Plot = createPlotlyComponent(Plotly)

export const StyledPlot = styled(Plot)({
  '& * .layer-above .imagelayer image': {
    clipPath: 'circle()',
  },
  '& * .modebar': {
    visibility: 'hidden',
  },
})

type BasePlotProps = {
  plotData?: CellPlotData[]
  shapes?: Partial<Shape>[]
  annotations?: Partial<Annotations>[]
  images?: Partial<Image>[]
  style?: React.CSSProperties
  isMultiPlotMode?: boolean
  comparisonField?: string
} & Pick<PlotParams, 'onRelayout' | 'onSelected' | 'onClick' | 'onHover'>

const BasePlotComponent = ({
  plotData = [],
  shapes = [],
  annotations = [],
  images = [],
  style,
  onClick,
  onRelayout,
  onSelected,
  onHover,
  isMultiPlotMode = false,
  comparisonField,
}: BasePlotProps): JSX.Element => {
  const { height: plotHeight } = usePanelHeight(0)
  const { range, setRange } = usePlotRange(isMultiPlotMode)
  const {
    cellVisualizations: { dataRevision, plotDisplayType, groupCompare },
  } = useCellVisualizationsSlice()
  const { isPreFilter } = useCellVisualizationUrlParams()
  const demoEnabled = useFlagCondition('demoEnabled')

  const showPreFilter = useMemo(() =>
    isPreFilter && !demoEnabled,
    [isPreFilter, demoEnabled]
  )

  // Hide colorbar when drag-drop is enabled to avoid layout shifts
  const dragDropEnabled = groupCompare?.dragDropEnabled ?? false

  const plotDataWithScale = usePlotDataWithScale(plotData, plotDisplayType, showPreFilter, dragDropEnabled)

  // Track previous dataRevision to detect actual data changes
  // Do NOT track mode changes - each mode has its own independent range
  const prevDataRevisionRef = useRef<number>()

  // Detect if data has changed during render phase (before plotLayout is calculated)
  const dataHasChanged = useMemo(() => {
    const dataRevisionChanged = prevDataRevisionRef.current !== undefined && prevDataRevisionRef.current !== dataRevision
    return dataRevisionChanged
  }, [dataRevision])

  // Clear mode-specific range when actual data changes (runs after render)
  useEffect(() => {
    if (dataHasChanged) {
      setRange(undefined)
    }
    // Update ref after processing
    prevDataRevisionRef.current = dataRevision
  }, [dataHasChanged, dataRevision, setRange])

  const plotStyle = useMemo(() => ({
    width: '100%',
    height: `${plotHeight}px`,
    position: 'relative' as const,
    top: '-5px',
    ...style,
  }), [plotHeight, style])

  const plotLayout = useMemo(() => {
    // Use existing mode-specific range from state if available (user has panned/zoomed)
    // BUT ignore it if data has changed (new dataset loaded)
    // Note: Mode-specific ranges are maintained independently - switching modes doesn't clear ranges
    let rangeX: [number, number] | undefined
    let rangeY: [number, number] | undefined

    if (!dataHasChanged && range?.x1 !== undefined && range?.x2 !== undefined && range?.y1 !== undefined && range?.y2 !== undefined) {
      // Use the existing mode-specific range from state (user's current view for this mode)
      rangeX = [range.x1, range.x2]
      rangeY = [range.y1, range.y2]
    } else if (comparisonField === 'leiden') {
      // Cluster multi-plot: Don't set range, let Plotly calculate it automatically
      // This works correctly for grid layouts without padding
      rangeX = undefined
      rangeY = undefined
    } else {
      // All other cases: single-plot OR non-cluster multi-plot
      // Calculate range with padding for proper margins
      const calculatedRange = calculatePlotRangeWithPadding(plotDataWithScale)
      rangeX = calculatedRange.rangeX
      rangeY = calculatedRange.rangeY
    }

    return {
      margin: { t: 0, b: 0, l: 0, r: 0, pad: 0 },
      shapes,
      xaxis: {
        visible: false,
        range: rangeX,
      },
      yaxis: {
        visible: false,
        scaleanchor: 'x' as const,
        scaleratio: 1,
        range: rangeY,
      },
      showlegend: false,
      clickmode: 'event' as const,
      annotations,
      uirevision: dataRevision,
      images,
      // this is only default dragmode. This gets changed internally because plotly is a bad boy and doesn't play by React's rules
      dragmode: 'pan' as const,
    }
  }, [shapes, annotations, dataRevision, images, range, dataHasChanged, plotDataWithScale, comparisonField])

  const plotConfig = useMemo(() => ({
    responsive: true,
    displaylogo: false,
    displayModeBar: true,
    scrollZoom: true,
  }), [])

  const handleUpdate = useMemo(() => onPlotUpdate(range, setRange), [range, setRange])

  return (
    <StyledPlot
      divId="scatter-plot"
      data={[...plotDataWithScale]}
      style={plotStyle}
      layout={plotLayout}
      onRelayout={onRelayout}
      onSelected={onSelected}
      onClick={onClick}
      onHover={onHover}
      onUpdate={handleUpdate}
      useResizeHandler
      config={plotConfig}
    />
  )
}

BasePlotComponent.displayName = 'BasePlot'

export const BasePlot = memo(BasePlotComponent)

export default BasePlot
